import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/config';
import '../css/SendSmsDialog.css';

// 대상자 한 명에게 문자를 보내는 다이얼로그.
//
// DashboardDetail 에도 같은 기능이 있지만 그쪽 모달은 케어플랜 링크 삽입·폐기가
// 얽혀 있어 그대로 가져오면 ControlCenter 에는 쓰지 않는 코드가 따라온다.
// 여기서는 발송에 필요한 것만 둔다.
//
// ⚠️ 전화번호는 이 컴포넌트가 직접 받아 온다. 로스터(/api/patients/roster)는
//    mobile 을 뽑지 않는다 — dec_text() 가 전원에 걸리는데 정작 쓰는 건
//    다이얼로그를 연 한 명뿐이라 일부러 뺐다(repository.js 참고).
export default function SendSmsDialog({ isOpen, onClose, appUserId, name, groupName }) {
    const [title, setTitle] = useState('');
    const [type, setType] = useState('공지');
    const [content, setContent] = useState('');
    const [mobile, setMobile] = useState(null);
    const [error, setError] = useState(null);
    const [isSending, setIsSending] = useState(false);
    const [history, setHistory] = useState(null);

    useEffect(() => {
        if (!isOpen || !appUserId) return;

        let cancelled = false;
        const load = async () => {
            setMobile(null);
            setError(null);
            setHistory(null);
            try {
                const token = localStorage.getItem('token');
                const headers = { Authorization: `Bearer ${token}` };

                const [detail, sent] = await Promise.all([
                    axios.get(`${API_BASE_URL}/api/health-data/${appUserId}`, { headers }),
                    axios.get(`${API_BASE_URL}/api/messages/history`, {
                        headers, params: { appUserId }
                    }).catch(() => null)
                ]);

                if (cancelled) return;

                const row = (detail.data?.data || [])[0];
                if (!row?.mobile) {
                    setError('이 대상자의 전화번호를 찾을 수 없어 문자를 보낼 수 없습니다.');
                    return;
                }
                setMobile(row.mobile);
                setHistory(sent ? (sent.data.history || []) : []);
            } catch (caught) {
                if (!cancelled) {
                    console.error('문자 발송 준비 실패:', caught.message);
                    setError('대상자 정보를 불러오지 못했습니다.');
                }
            }
        };

        load();
        return () => { cancelled = true; };
    }, [isOpen, appUserId]);

    if (!isOpen) return null;

    const canSend = Boolean(mobile) && title.trim() && content.trim() && !isSending;

    const handleSend = async () => {
        if (!canSend) return;
        setIsSending(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API_BASE_URL}/api/messages/send-direct`, {
                message_type: type,
                title,
                content,
                sender: localStorage.getItem('user_name') || '관리자',
                recipient: name,
                // 병원 연락처 꼬리말 판정과 발송 이력 연결에 쓰인다.
                app_user_id: appUserId,
                created_id: parseInt(localStorage.getItem('id'), 10),
                created_at: new Date().toISOString(),
                scheduled_date: null,
                sent_date: new Date().toISOString(),
                messages: [{ to: mobile, content }]
            }, { headers: { Authorization: `Bearer ${token}` } });

            alert('문자가 성공적으로 발송되었습니다.');
            setTitle('');
            setContent('');
            onClose();
        } catch (caught) {
            console.error('문자 발송 실패:', caught.message);
            alert('문자 발송 중 오류가 발생했습니다.');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <>
            <div className="send-sms-dialog">
                <div className="send-sms-head">
                    <h3>문자 메시지 발송</h3>
                    <button className="send-sms-close" onClick={onClose}>×</button>
                </div>

                <div className="send-sms-subject">
                    {name}
                    {groupName && groupName !== '-' && <span className="send-sms-group">{groupName}</span>}
                    {mobile && <span className="send-sms-mobile">{mobile}</span>}
                </div>

                {error && <div className="send-sms-error">{error}</div>}

                <label className="send-sms-field">
                    <span>메시지 제목</span>
                    <input
                        type="text"
                        value={title}
                        onChange={(event) => setTitle(event.target.value)}
                        placeholder="예) [서울드림의원]"
                    />
                </label>

                <label className="send-sms-field">
                    <span>메시지 구분</span>
                    <input
                        type="text"
                        value={type}
                        onChange={(event) => setType(event.target.value)}
                    />
                </label>

                <label className="send-sms-field">
                    <span>메시지 내용</span>
                    <textarea
                        value={content}
                        onChange={(event) => setContent(event.target.value)}
                        placeholder="메시지 내용을 입력해주세요"
                        rows={5}
                    />
                </label>

                {/* 무엇을 이미 보냈는지 모르고 다시 쓰는 일을 줄인다. */}
                <div className="send-sms-history">
                    <div className="send-sms-history-head">보낸 문자</div>
                    {history === null ? (
                        <div className="send-sms-history-empty">불러오는 중…</div>
                    ) : history.length === 0 ? (
                        <div className="send-sms-history-empty">
                            보낸 문자가 없습니다.
                            <span className="send-sms-history-note">
                                2026-08-12 이전 발송분은 대상자와 연결돼 있지 않아 표시되지 않습니다.
                            </span>
                        </div>
                    ) : (
                        <ul className="send-sms-history-list">
                            {history.map((entry) => (
                                <li key={entry.id}>
                                    <div className="send-sms-history-meta">
                                        <span className="send-sms-history-date">
                                            {entry.sent_date || entry.scheduled_date || '-'}
                                        </span>
                                        {entry.message_type && (
                                            <span className="send-sms-history-type">{entry.message_type}</span>
                                        )}
                                    </div>
                                    <div className="send-sms-history-title">{entry.title}</div>
                                    {/* 무엇을 보냈는지 보려면 제목만으로는 부족하다.
                                        길면 CSS 에서 세 줄까지만 보인다. */}
                                    <div className="send-sms-history-content">{entry.content}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                <div className="send-sms-actions">
                    <button className="send-sms-cancel" onClick={onClose}>취소</button>
                    <button className="send-sms-submit" onClick={handleSend} disabled={!canSend}>
                        {isSending ? '전송 중…' : '발송'}
                    </button>
                </div>
            </div>
            <div className="send-sms-overlay" onClick={onClose} />
        </>
    );
}
